import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import Collapse from "@mui/material/Collapse"
import Typography from "@mui/material/Typography"
import { ExpandLess, ExpandMore } from "@mui/icons-material"
import { useState } from "react"
import { Grid, List, ListItemText } from "@mui/material"
import { GraphDto } from "@custom-types/graphs"
import {
  getLeftRows,
  getMiddleRows,
  getRightRows,
} from "@features/scenarioDesign/GraphMetadataCard/helpers"
import InfoIcon from "@mui/icons-material/Info"
import ListItem from "@mui/material/ListItem"

type GraphMetadataCardProps = {
  graph: GraphDto
}

const GraphMetadataCard = ({ graph }: GraphMetadataCardProps) => {
  const [isExpanded, setIsExpanded] = useState(true)
  const [isExpandedData, setIsExpandedData] = useState(false)

  const handleExpandCollapseClick = () => {
    setIsExpanded(!isExpanded)
  }

  const handleExpandCollapseDataClick = () => {
    setIsExpandedData(!isExpandedData)
  }

  const leftColumnRows = getLeftRows(graph)
  const middleColumnRows = getMiddleRows(graph)
  const rightColumnRows = getRightRows(graph)

  return (
    <Card sx={{ width: "100%", boxShadow: "none" }}>
      <CardContent
        sx={{
          paddingBottom: "5px !important",
          padding: "5px",
          mb: "0px",
          cursor: "pointer",
          ":hover": { backgroundColor: "#f7f7f7" },
          borderBottom: "1px solid #e7e7e7",
        }}
        onClick={handleExpandCollapseClick}
      >
        {isExpanded ? (
          <ExpandMore sx={{ verticalAlign: "middle", marginTop: "-3px" }} />
        ) : (
          <ExpandLess sx={{ verticalAlign: "middle", marginTop: "-3px" }} />
        )}
        <InfoIcon sx={{ verticalAlign: "middle", marginTop: "-5px" }} />
        <Typography variant="h6" component="span" sx={{ ml: 1 }}>
          <b>Graph Metadata</b>
        </Typography>
      </CardContent>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <CardContent
          sx={{
            paddingBottom: "5px !important",
            padding: "5px",
            mb: "0px",
            ":hover": { backgroundColor: "#f7f7f7" },
          }}
        >
          <Grid container>
            <Grid item container direction="column" xs={4}>
              {leftColumnRows.map(({ label, value }) => (
                <Grid item container key={label}>
                  <Grid item sx={{ width: "120px" }}>
                    <Typography component="span" sx={{ fontWeight: "bold" }}>
                      {label}:
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography component="span">{value || "-"}</Typography>
                  </Grid>
                </Grid>
              ))}
            </Grid>

            <Grid item container direction="column" xs={4}>
              {middleColumnRows.map(({ label, value }) => (
                <Grid item container key={label}>
                  <Grid item sx={{ width: "120px" }}>
                    <Typography component="span" sx={{ fontWeight: "bold" }}>
                      {label}:
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography component="span">{value || "-"}</Typography>
                  </Grid>
                </Grid>
              ))}
            </Grid>

            <Grid item container direction="column" xs={4}>
              {rightColumnRows.map(({ label, value }) => (
                <Grid item container key={label}>
                  <Grid item sx={{ width: "160px" }}>
                    <Typography component="span" sx={{ fontWeight: "bold" }}>
                      {label}:
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography component="span">{value || "-"}</Typography>
                  </Grid>
                </Grid>
              ))}
            </Grid>

            <Card sx={{ width: "100%", mt: 1, border: "1px solid #ccc", boxShadow: "none" }}>
              <CardContent
                sx={{
                  paddingBottom: "5px !important",
                  padding: "5px",
                  mb: "0px",
                  cursor: "pointer",
                  ":hover": { backgroundColor: "#f7f7f7" },
                  borderBottom: "1px solid #e7e7e7",
                }}
                onClick={handleExpandCollapseDataClick}
              >
                {isExpandedData ? (
                  <ExpandMore sx={{ verticalAlign: "middle" }} />
                ) : (
                  <ExpandLess sx={{ verticalAlign: "middle" }} />
                )}
                <Typography variant="body1" component="span" sx={{ ml: 1 }}>
                  <b>Data & Scenarios</b>
                </Typography>
              </CardContent>

              <Collapse in={isExpandedData} timeout="auto" unmountOnExit>
                <CardContent
                  sx={{
                    paddingBottom: "5px !important",
                    padding: "5px",
                    mb: "0px",
                    ":hover": { backgroundColor: "#f7f7f7" },
                  }}
                >
                  <Grid item container direction="column" xs={12} gap={1}>
                    <Grid item container key={"description"}>
                      <Grid item sx={{ width: "160px" }}>
                        <Typography component="span" sx={{ fontWeight: "bold" }}>
                          Description:
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Typography component="span">{graph.description || "-"}</Typography>
                      </Grid>
                    </Grid>
                    <Grid item container key={"datasource"}>
                      <Grid item sx={{ width: "160px" }}>
                        <Typography component="span" sx={{ fontWeight: "bold" }}>
                          Data Source:
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Typography component="span">{graph.scenario_source || "-"}</Typography>
                      </Grid>
                    </Grid>
                    <Grid item container key={"scenarios"}>
                      {graph.scenarios.length > 0 && (
                        <Grid item xs>
                          <Typography fontWeight="bold">Pre-defined scenarios:</Typography>
                          <List>
                            {graph.scenarios.map((p) => (
                              <ListItem
                                divider
                                key={p}
                                sx={{
                                  width: "100%",
                                  cursor: "pointer",
                                  "&:hover": {
                                    backgroundColor: "rgba(225, 225, 225, 0.4)",
                                  },
                                }}
                              >
                                <ListItemText
                                  primaryTypographyProps={{ fontSize: "14px" }}
                                  primary={p}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Collapse>
            </Card>
          </Grid>
        </CardContent>
      </Collapse>
    </Card>
  )
}

export default GraphMetadataCard
